import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { clsx } from 'clsx';
import { useBreakpoints, WebLocation } from '../../customHooks/useBreakpoints';
import { iconToCdn } from '../../utils/images';
import { IconAxel } from '../../resources/images';

interface InfoBoxProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    children: React.ReactNode;
    titleIcon?: 'info';
    color?: 'info' | 'warning' | 'error' | 'green';
    noMargin?: boolean;
}

const InfoBox: FC<InfoBoxProps> = ({ children, color = 'info', className, noMargin, ...rest }): ReactElement => {
    const classes = useStyles();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    return (
        <div
            className={clsx(
                classes.box,
                isMobileSize && classes.marginResponsive,
                classes[color],
                className,
                noMargin && classes.noMargin,
            )}
            {...rest}
        >
            {children}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        box: {
            flex: 1,
            display: 'flex',
            borderRadius: 10,
            padding: '16px 9px 16px 16px',
            justifyContent: 'start',
            alignItems: 'center',
            marginRight: theme.spacing(3.75),
            marginLeft: theme.spacing(3.75),
            position: 'relative',
        },
        marginResponsive: {
            marginRight: 20,
            marginLeft: 20,
        },
        noMargin: {
            marginRight: 0,
            marginLeft: 0,
        },
        info: {
            background: `linear-gradient(0deg, ${theme.palette.colors.border}, ${theme.palette.colors.border}), linear-gradient(0deg, rgba(69, 112, 234, 0.2), rgba(69, 112, 234, 0.2)), #FFFFFF`,
        },
        warning: {
            padding: 12,
            background: `${theme.palette.colors.text.warning}0F`,
            color: `${theme.palette.colors.text.warning} !important`,
        },
        error: {
            background: `${theme.palette.colors.text.error}1F`,
            color: `${theme.palette.colors.text.error} !important`,
        },
        green: {
            background: 'linear-gradient(86.74deg, #119957 -25.89%, #0B6339 96.53%)',
            color: `${theme.palette.primary.contrastText} !important`,

            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '5px', // Distance from the bottom edge
                right: '15px', // Distance from the right edge
                width: '110px', // Adjust based on the icon size
                height: '90px', // Adjust based on the icon size

                maskImage: `url('${iconToCdn(IconAxel.Leaf)}')`,
                maskRepeat: 'no-repeat',
                maskSize: 'contain',

                WebkitMaskImage: `url('${iconToCdn(IconAxel.Leaf)}')`,
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',

                backgroundColor: '#FFFFFF80',
            },
        },
    }),
);

export default InfoBox;
